import React, { useLayoutEffect } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import CheckStatus from './components/CheckStatus';
import SocialFloatingButton from './components/SocialFloatingButton';
import PaymentSuccessPage from './components/PaymentSuccessPage';
import MyPage from './components/MyPage';
import NarniaTalkWidget from './components/NarniaTalkWidget';
import ProductPage from './components/ProductPage';
import EducationPage from './components/EducationPage';
import HomePage from './pages/home/HomePage';
import HomeApplicationFloatingButton from './pages/home/HomeApplicationFloatingButton';
import ShowcasePage from './pages/showcase/ShowcasePage';
import { FaInstagram } from 'react-icons/fa';
import { RiKakaoTalkFill } from 'react-icons/ri';
import './App.css';

const AppContent = () => {
  const location = useLocation();
  const isPaymentPage = location.pathname.startsWith('/payment');
  const isHomepage = location.pathname === '/';
  const isEducationPage = location.pathname === '/education';
  const isShowcasePage = location.pathname === '/showcase';
  const hasLightHeader = isHomepage || isEducationPage || isShowcasePage;
  const hasGlobalChrome = !isPaymentPage;
  const hasLegacyStatusButton = hasGlobalChrome && !hasLightHeader;

  useLayoutEffect(() => {
    if (location.hash) return;

    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;

    // 페이지 전환 직후 이전 페이지의 스크롤 위치가 한 프레임 보이지 않도록
    // CSS의 smooth scroll을 잠시 해제하고 페인트 전에 상단으로 이동합니다.
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    root.style.scrollBehavior = previousScrollBehavior;
  }, [location.hash, location.key, location.pathname]);

  return (
    <div className="App">
      <Helmet>
        <title>나니아랩 | AI 엔지니어 교육</title>
        <meta
          name="description"
          content="나니아랩 AI 엔지니어 교육: 정답 찾기에서 '질문 만들기'로, AI를 지배하는 인재를 양성합니다."
        />
        <link rel="canonical" href="https://narnia-lab.com" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="나니아랩 | AI 엔지니어 교육" />
        <meta property="og:description" content="나니아랩 AI 엔지니어 교육: 정답 찾기에서 '질문 만들기'로, AI를 지배하는 인재를 양성합니다." />
        <meta property="og:image" content="https://narnia-lab.com/logo.jpg" />
        <meta property="og:url" content="https://narnia-lab.com" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://narnia-lab.com" />
        <meta property="twitter:title" content="나니아랩 | AI 엔지니어 교육" />
        <meta property="twitter:description" content="나니아랩 AI 엔지니어 교육: 정답 찾기에서 '질문 만들기'로, AI를 지배하는 인재를 양성합니다." />
        <meta property="twitter:image" content="https://narnia-lab.com/logo.jpg" />
      </Helmet>

      {hasGlobalChrome && <Header variant={hasLightHeader ? 'light' : 'dark'} />}

      {/* Global Floating Buttons */}
      {hasLegacyStatusButton && <CheckStatus />}

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/mypage" element={<MyPage />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/education" element={<EducationPage />} />
        <Route path="/showcase" element={<ShowcasePage />} />
        <Route path="/concept/*" element={<Navigate to="/" replace />} />
      </Routes>

      {hasGlobalChrome && <Footer />}

      {/* Social Floating Buttons */}
      {hasGlobalChrome && (
        <>
          {isHomepage && <HomeApplicationFloatingButton />}

          {/* KakaoTalk Floating Button */}
          <SocialFloatingButton
            href="https://pf.kakao.com/_DZhmn"
            icon={RiKakaoTalkFill}
            text="카카오톡"
            bottom="var(--floating-action-slot-2)"
            right="var(--floating-ui-right)"
            background="#FEE500"
            textColor="#191919"
            zIndex={1001}
            ariaLabel="카카오톡 채널 바로가기"
            openInNewTab={true}
          />

          {/* Instagram Floating Button */}
          <SocialFloatingButton
            href="https://www.instagram.com/narnialab_official?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
            icon={FaInstagram}
            text="인스타그램"
            bottom="var(--floating-action-slot-1)"
            right="var(--floating-ui-right)"
            background="linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)"
            textColor="white"
            zIndex={1001}
            ariaLabel="인스타그램 바로가기"
            openInNewTab={true}
          />

          {/* 나니아 톡 - AI 상담 위젯 */}
          <NarniaTalkWidget variant={hasLightHeader ? 'apple' : 'default'} />
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
